/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */
/** @jsx jsx */

import { jsx } from '@emotion/react'
import React, { Fragment } from 'react'

import {
  EuiFlexItem,
  EuiSpacer,
  EuiFlexGrid,
  EuiSkeletonText,
  useEuiTheme,
  EuiShowFor,
  EuiFlexGroup,
} from '@elastic/eui'

import useBillingDetails from '@modules/billing-lib/billingDetails/useBillingDetails'
import { useConfig } from '@modules/cui/ConfigContext'

import OrganizationPrepaids from '../components/OrganizationPrepaids'
import SubscriptionDetailsCard from '../components/SubscriptionDetailsCard'
import SuggestedContentPanel from '../components/SuggestedContentPanel'
import UsageCardV2 from '../components/UsageCardV2'
import PrepaidAccountDetailsPanel from '../components/PrepaidAccountDetailsPanel'
import PaymentDetailsCard from '../components/PaymentDetailsCard'
import { gridContainerCss, gridItemCss } from '../styles'
import PlanOverviewPanel from '../components/PlanOverviewPanel'

interface MarketplacesOverviewProps {
  organizationId: string
}

const MarketplacesOverview: React.FunctionComponent<MarketplacesOverviewProps> = ({
  organizationId,
}) => {
  const theme = useEuiTheme()
  const billingDetails = useBillingDetails()
  const isAdminConsole = useConfig('APP_NAME') === 'adminconsole'

  if (!billingDetails.data) {
    return <EuiSkeletonText />
  }

  return (
    <EuiFlexGrid gutterSize='m' css={gridContainerCss(theme)}>
      <EuiShowFor sizes={['xl']}>
        <EuiFlexItem>
          <UsageCardV2 organizationId={organizationId} />
        </EuiFlexItem>

        <EuiFlexItem>
          <PlanOverviewPanel organizationId={organizationId} />
        </EuiFlexItem>

        <EuiFlexItem>
          <SubscriptionDetailsCard />
        </EuiFlexItem>

        <EuiFlexItem grow={false}>
          <EuiFlexGroup direction='column' gutterSize='m'>
            <EuiFlexItem grow={false}>
              <PaymentDetailsCard organizationId={organizationId} />
            </EuiFlexItem>
            <EuiFlexItem grow={false}>
              <SuggestedContentPanel
                hasBorder={true}
                data-test-id='billing-details-summary.suggested-content'
                showEcuMessage={true}
              />
            </EuiFlexItem>
          </EuiFlexGroup>
        </EuiFlexItem>

        <EuiFlexItem css={gridItemCss(theme)}>
          <PrepaidAccountDetailsPanel hasBorder={true} organizationId={organizationId} />
          <EuiSpacer size='m' />
          {isAdminConsole && <OrganizationPrepaids organizationId={organizationId} />}
        </EuiFlexItem>
      </EuiShowFor>
      {/*Tablet view has a different layout */}
      <EuiShowFor sizes={['xs', 's', 'm', 'l']}>
        <EuiFlexItem>
          <UsageCardV2 organizationId={organizationId} />
        </EuiFlexItem>
        <EuiFlexItem>
          <PlanOverviewPanel organizationId={organizationId} />
        </EuiFlexItem>
        <EuiFlexItem>
          <PaymentDetailsCard organizationId={organizationId} />
        </EuiFlexItem>
        <EuiFlexItem>
          <SubscriptionDetailsCard />
        </EuiFlexItem>
        <EuiFlexItem css={gridItemCss(theme)}>
          <PrepaidAccountDetailsPanel hasBorder={true} organizationId={organizationId} />
          {isAdminConsole && (
            <Fragment>
              <EuiSpacer size='m' />
              <OrganizationPrepaids organizationId={organizationId} />
            </Fragment>
          )}
        </EuiFlexItem>
        <EuiFlexItem>
          <SuggestedContentPanel
            hasBorder={true}
            data-test-id='billing-details-summary.suggested-content'
            showEcuMessage={true}
          />
        </EuiFlexItem>
      </EuiShowFor>
    </EuiFlexGrid>
  )
}

export default MarketplacesOverview
